import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { CodeFormBrandType } from './codeTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.code.style, {
  button: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.code.tags.bgCopyButton'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.code.tags.hoverColorButton'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  defaultIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.code.tags.bgCopyIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.code.tags.colorCopyIcon'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  successIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.code.tags.bgSuccessCopyIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.code.tags.colorCopyIcon'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icon: {
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 100, showInput: true },
    }),
  },
})

const CodeFormStyle: CodeFormBrandType = ({ device, subConfigs }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.code.tags.label')}</Typography>
        <FormField config={schema.button.backgroundColor} />
        <FormField config={schema.button.color} />

        <FormField config={schema.defaultIcon.backgroundImage} />
        <FormField config={schema.defaultIcon.color} />

        <FormField config={schema.successIcon.backgroundImage} />
        <FormField config={schema.successIcon.color} />

        <FormField config={schema.icon.height} />
        <FormField config={schema.icon.width} />
        <FormField config={schema.icon.borderRadius} />
      </Padding>
    </LayoutScroll>
  )
}

export default CodeFormStyle
